import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  Typography,
  Stack,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import RestoreRoundedIcon from "@mui/icons-material/RestoreRounded";
import {
  getCommissionConfig,
  setCommissionConfig,
  getAllCommissionOverrides,
  getUserCommissionChain,
  setCommissionOverride,
  deleteCommissionOverride,
  adminListUsers,
  getFinkedaGateways,
} from "../api";

const ROLES = [
  { level: 1, name: "ADMIN" },
  { level: 2, name: "ADMIN_PARTNER" },
  { level: 3, name: "WHITE_LABEL" },
  { level: 4, name: "STATE_HEAD" },
  { level: 5, name: "SUPER_DISTRIBUTOR" },
  { level: 6, name: "MASTER_DISTRIBUTOR" },
  { level: 7, name: "DISTRIBUTOR" },
  { level: 8, name: "RETAILER" },
  { level: 9, name: "SHOPKEEPER" },
];

/* BiznessPay payin runs on the Cashfree provider, surfaced as "Pure Travel".
   Commission overrides MUST be stored under "cashfree" so they match the
   payin's gateway when commission is calculated. */
const GATEWAYS = [
  { value: "default", label: "Default (all gateways)" },
  { value: "cashfree", label: "Pure Travel" },
  /* Offline collections — each medium carries its own rate and its own
     commission wallet, so they are configured separately. */
  { value: "pinelabs_pos", label: "POS machine" },
  { value: "pinelabs_qr", label: "QR code" },
];

/* ================================================================== */

export default function AdminCommissions() {
  const [tab, setTab] = useState(0);

  return (
    <Box>
      <Typography variant="h5" mb={1}>
        Commission Configuration
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Set default rates per role and override for specific users
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Default Rates" />
        <Tab label="Per-User Overrides" />
      </Tabs>

      {tab === 0 && <DefaultRates />}
      {tab === 1 && <UserOverrides />}
    </Box>
  );
}

/* ================================================================== */
/*  TAB 1 — Default Commission Rates                                  */
/* ================================================================== */

function DefaultRates() {
  const [configs, setConfigs] = useState([]);
  const [gateway, setGateway] = useState("default");
  const [usingFallback, setUsingFallback] = useState(false);
  const [rates, setRates] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const loadConfig = useCallback(() => {
    setLoading(true);
    getCommissionConfig()
      .then(({ data }) => setConfigs(data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  /* Rebuild the editable grid whenever the gateway or configs change.
     Show rows matching the selected gateway; fall back to "default" rows
     when the gateway has none (but saving still writes the selected gateway). */
  useEffect(() => {
    const rowsFor = (gw) =>
      (configs || []).filter((c) => (c.gateway || "default") === gw);
    let rows = rowsFor(gateway);
    const fallback = gateway !== "default" && rows.length === 0;
    if (fallback) rows = rowsFor("default");
    setUsingFallback(fallback);
    const map = {};
    rows.forEach((c) => {
      map[`${c.transaction_role_level}_${c.beneficiary_role_level}`] = String(
        c.percentage,
      );
    });
    setRates(map);
  }, [gateway, configs]);

  const handleChange = (txnLevel, benLevel, value) => {
    setRates((prev) => ({ ...prev, [`${txnLevel}_${benLevel}`]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMsg(null);
    const rateEntries = [];

    for (const key of Object.keys(rates)) {
      const val = parseFloat(rates[key]);
      if (isNaN(val) || val < 0) continue;
      const [txn, ben] = key.split("_").map(Number);
      rateEntries.push({
        transactionRoleLevel: txn,
        beneficiaryRoleLevel: ben,
        percentage: val,
        gateway,
      });
    }

    try {
      const { data } = await setCommissionConfig(rateEntries);
      setMsg({ severity: "success", text: data.message || "Saved!" });
      loadConfig();
    } catch (e) {
      setMsg({
        severity: "error",
        text: e.response?.data?.error || "Failed to save",
      });
    }
    setSaving(false);
  };

  if (loading)
    return (
      <Box sx={{ p: 5, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );

  const txnRoles = ROLES.filter((r) => r.level >= 2);

  return (
    <Card sx={{ p: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        flexWrap="wrap"
        gap={2}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            Commission Rates
          </Typography>
          <Typography variant="body2" color="text.secondary">
            When a user at [Row role] makes a payment, each ancestor at [Column
            role] earns the configured %
          </Typography>
        </Box>
        <Stack direction="row" gap={1.5} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Gateway</InputLabel>
            <Select
              value={gateway}
              label="Gateway"
              onChange={(e) => setGateway(e.target.value)}
            >
              {GATEWAYS.map((g) => (
                <MenuItem key={g.value} value={g.value}>
                  {g.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<SaveRoundedIcon />}
            onClick={handleSave}
            disabled={saving}
            sx={{ borderRadius: 3 }}
          >
            {saving ? "Saving…" : "Save All"}
          </Button>
        </Stack>
      </Stack>

      {usingFallback && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No specific rates set for{" "}
          <strong>
            {GATEWAYS.find((g) => g.value === gateway)?.label || gateway}
          </strong>{" "}
          yet — showing the Default rates. Editing and saving here will create
          rates specific to this gateway.
        </Alert>
      )}

      {msg && (
        <Alert
          severity={msg.severity}
          sx={{ mb: 2 }}
          onClose={() => setMsg(null)}
        >
          {msg.text}
        </Alert>
      )}

      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 700,
                  bgcolor: "grey.100",
                  position: "sticky",
                  left: 0,
                  zIndex: 2,
                }}
              >
                When ↓ pays
              </TableCell>
              {ROLES.filter((r) => r.level <= 8).map((ben) => (
                <TableCell
                  key={ben.level}
                  align="center"
                  sx={{
                    fontWeight: 600,
                    fontSize: 11,
                    whiteSpace: "nowrap",
                    bgcolor: "grey.50",
                  }}
                >
                  {ben.name.replace(/_/g, " ")}
                  <br />
                  <Typography variant="caption" color="text.secondary">
                    Lv {ben.level}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {txnRoles.map((txn) => (
              <TableRow key={txn.level}>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: 12,
                    whiteSpace: "nowrap",
                    bgcolor: "grey.50",
                    position: "sticky",
                    left: 0,
                    zIndex: 1,
                  }}
                >
                  {txn.name.replace(/_/g, " ")}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Level {txn.level}
                  </Typography>
                </TableCell>
                {ROLES.filter((r) => r.level <= 8).map((ben) => {
                  const valid = ben.level < txn.level;
                  const key = `${txn.level}_${ben.level}`;
                  return (
                    <TableCell key={ben.level} align="center" sx={{ p: 0.5 }}>
                      {valid ? (
                        <TextField
                          size="small"
                          type="number"
                          inputProps={{
                            min: 0,
                            max: 100,
                            step: 0.01,
                            style: { textAlign: "center", fontSize: 13 },
                          }}
                          value={rates[key] ?? ""}
                          onChange={(e) =>
                            handleChange(txn.level, ben.level, e.target.value)
                          }
                          placeholder="0"
                          sx={{ width: 72 }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Alert severity="info" sx={{ mt: 2 }}>
        Example: If RETAILER (Lv 8) row × DISTRIBUTOR (Lv 7) column = 2%, then
        every time a Retailer makes a payin, their Distributor parent earns 2%
        of the payment.
      </Alert>
    </Card>
  );
}

/* ================================================================== */
/*  TAB 2 — Per-User Commission Overrides                             */
/* ================================================================== */

function UserOverrides() {
  const [allOverrides, setAllOverrides] = useState([]);
  const [loadingAll, setLoadingAll] = useState(true);

  const [gatewayOptions, setGatewayOptions] = useState(GATEWAYS);
  const [selectedGateway, setSelectedGateway] = useState("default");

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const [selectedUser, setSelectedUser] = useState(null);
  const [chain, setChain] = useState([]);
  const [chainUser, setChainUser] = useState(null);
  const [loadingChain, setLoadingChain] = useState(false);

  const [editDialog, setEditDialog] = useState(null);
  const [editPct, setEditPct] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  const [msg, setMsg] = useState(null);

  const fetchOverrides = useCallback(() => {
    setLoadingAll(true);
    getAllCommissionOverrides()
      .then(({ data }) => setAllOverrides(data || []))
      .catch(() => {})
      .finally(() => setLoadingAll(false));
  }, []);

  useEffect(() => {
    fetchOverrides();
  }, [fetchOverrides]);

  /* Fetch Finkeda gateways once and append them to the selector options.
     If the fetch fails, silently keep the built-in gateway list. */
  useEffect(() => {
    getFinkedaGateways()
      .then(({ data }) => {
        /* The endpoint returns the gateways array directly as the body. */
        const list = Array.isArray(data) ? data : data?.gateways || [];
        const finkeda = list.map((g) => ({
          value: g.gatewayKey,
          label: `Finkeda · ${g.purpose}`,
        }));
        if (finkeda.length) setGatewayOptions([...GATEWAYS, ...finkeda]);
      })
      .catch(() => {});
  }, []);

  const gatewayLabel = (value) =>
    gatewayOptions.find((g) => g.value === value)?.label || value;

  /* Current override % for an ancestor scoped to the selected gateway
     (sourced from getAllCommissionOverrides, not the generic default row). */
  const gatewayOverrideFor = useCallback(
    (ancestorUserId) => {
      if (!selectedUser) return null;
      const row = allOverrides.find(
        (o) =>
          String(o.user_id) === String(selectedUser.id) &&
          String(o.beneficiary_user_id) === String(ancestorUserId) &&
          (o.gateway || "default") === selectedGateway,
      );
      return row != null ? Number(row.percentage) : null;
    },
    [allOverrides, selectedUser, selectedGateway],
  );

  const visibleOverrides = allOverrides.filter(
    (o) => (o.gateway || "default") === selectedGateway,
  );

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const { data } = await adminListUsers({
        search: searchQuery.trim(),
        page: 1,
        limit: 10,
      });
      setSearchResults(data.users || []);
    } catch {
      setSearchResults([]);
    }
    setSearching(false);
  };

  const handleSelectUser = async (user) => {
    setSelectedUser(user);
    setSearchResults([]);
    setSearchQuery("");
    setLoadingChain(true);
    try {
      const { data } = await getUserCommissionChain(user.id);
      setChainUser(data.user);
      setChain(data.chain || []);
    } catch {
      setChain([]);
    }
    setLoadingChain(false);
  };

  const openEdit = (ancestor) => {
    setEditDialog(ancestor);
    const gwOverride = gatewayOverrideFor(ancestor.userId);
    setEditPct(
      gwOverride != null ? String(gwOverride) : String(ancestor.defaultRate),
    );
  };

  const handleSaveOverride = async () => {
    if (!editDialog || !selectedUser) return;
    const pct = parseFloat(editPct);
    if (isNaN(pct) || pct < 0 || pct > 100) return;
    setEditSaving(true);
    try {
      await setCommissionOverride(
        selectedUser.id,
        editDialog.userId,
        pct,
        selectedGateway,
      );
      setMsg({ severity: "success", text: "Override saved!" });
      setEditDialog(null);
      handleSelectUser(selectedUser);
      fetchOverrides();
    } catch (e) {
      setMsg({
        severity: "error",
        text: e.response?.data?.error || "Failed to save override",
      });
    }
    setEditSaving(false);
  };

  const handleDeleteOverride = async (ancestor) => {
    if (!selectedUser) return;
    try {
      await deleteCommissionOverride(
        selectedUser.id,
        ancestor.userId,
        selectedGateway,
      );
      setMsg({
        severity: "success",
        text: "Override removed — default rate will apply",
      });
      handleSelectUser(selectedUser);
      fetchOverrides();
    } catch (e) {
      setMsg({
        severity: "error",
        text: e.response?.data?.error || "Failed to delete",
      });
    }
  };

  return (
    <Stack spacing={3}>
      {msg && (
        <Alert severity={msg.severity} onClose={() => setMsg(null)}>
          {msg.text}
        </Alert>
      )}

      {/* ---- Search & select user ---- */}
      <Card sx={{ p: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} mb={1}>
          Set Override for a Specific User
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Search for a user who makes payments (swiper). You'll see their parent
          chain and can set custom commission rates for each ancestor.
        </Typography>

        <FormControl size="small" sx={{ minWidth: 240, mb: 1 }}>
          <InputLabel>Gateway</InputLabel>
          <Select
            value={selectedGateway}
            label="Gateway"
            onChange={(e) => setSelectedGateway(e.target.value)}
          >
            {gatewayOptions.map((g) => (
              <MenuItem key={g.value} value={g.value}>
                {g.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="caption" color="text.secondary" display="block" mb={2}>
          Overrides are per gateway — pick a gateway, then set each ancestor's %.
          Finkeda gateways each have their own rates.
        </Typography>

        <Stack direction="row" gap={1} alignItems="flex-start">
          <TextField
            size="small"
            placeholder="Search by name or email…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            sx={{ flex: 1 }}
          />
          <Button
            variant="contained"
            startIcon={<SearchRoundedIcon />}
            onClick={handleSearch}
            disabled={searching}
            sx={{ borderRadius: 3 }}
          >
            Search
          </Button>
        </Stack>

        {searchResults.length > 0 && (
          <Box sx={{ overflowX: "auto", mt: 2 }}>
            <Table size="small" sx={{ minWidth: 420 }}>
              <TableBody>
              {searchResults.map((u) => (
                <TableRow
                  key={u.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => handleSelectUser(u)}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {u.first_name} {u.last_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {u.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={u.role_name?.replace(/_/g, " ")}
                      sx={{ fontWeight: 600, fontSize: 11 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Button size="small" sx={{ textTransform: "none" }}>
                      Select
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Card>

      {/* ---- Selected user's commission chain ---- */}
      {selectedUser && (
        <Card sx={{ p: 3 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                Commission Chain for:{" "}
                {selectedUser.first_name || selectedUser.email}{" "}
                {selectedUser.last_name || ""}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {chainUser?.roleName?.replace(/_/g, " ") ||
                  selectedUser.role_name?.replace(/_/g, " ")}
                {" (Level "}
                {chainUser?.roleLevel || ""}
                {") "}— When this user makes a payment, each ancestor below
                earns commission
              </Typography>
            </Box>
            <Button
              size="small"
              onClick={() => {
                setSelectedUser(null);
                setChain([]);
                setChainUser(null);
              }}
              sx={{ textTransform: "none" }}
            >
              Clear
            </Button>
          </Stack>

          {loadingChain ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <CircularProgress size={28} />
            </Box>
          ) : chain.length === 0 ? (
            <Alert severity="info">
              No parent chain found. This user may be at the top of the
              hierarchy.
            </Alert>
          ) : (
            <Box sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Ancestor</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                    Default Rate
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                    Override
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                    Effective
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {chain.map((a) => {
                  const overrideRate = gatewayOverrideFor(a.userId);
                  const effective =
                    overrideRate != null ? overrideRate : a.defaultRate;
                  return (
                    <TableRow key={a.userId} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {a.firstName} {a.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {a.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={a.roleName?.replace(/_/g, " ")}
                          sx={{ fontWeight: 600, fontSize: 11 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" color="text.secondary">
                          {a.defaultRate}%
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {overrideRate != null ? (
                          <Chip
                            size="small"
                            label={`${overrideRate}%`}
                            color="warning"
                            sx={{ fontWeight: 700 }}
                          />
                        ) : (
                          <Typography variant="body2" color="text.disabled">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color="primary.main"
                        >
                          {effective}%
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Stack
                          direction="row"
                          gap={0.5}
                          justifyContent="flex-end"
                        >
                          <Tooltip title="Set custom rate">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => openEdit(a)}
                            >
                              <EditRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {overrideRate != null && (
                            <Tooltip title="Remove override (revert to default)">
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => handleDeleteOverride(a)}
                              >
                                <RestoreRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </Box>
          )}
        </Card>
      )}

      {/* ---- All existing overrides ---- */}
      <Card sx={{ p: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} mb={1}>
          All Active Overrides — {gatewayLabel(selectedGateway)}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Users with custom commission rates for the selected gateway
          (overriding the default matrix)
        </Typography>

        {loadingAll ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <CircularProgress size={28} />
          </Box>
        ) : visibleOverrides.length === 0 ? (
          <Alert severity="info">
            No overrides set for {gatewayLabel(selectedGateway)}. These users use
            the default rates.
          </Alert>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 640 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Swiper (payer)</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  Beneficiary (earner)
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Gateway</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  Override %
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleOverrides.map((o) => (
                <TableRow key={o.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {o.swiper_first_name} {o.swiper_last_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {o.swiper_email} • {o.swiper_role?.replace(/_/g, " ")}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {o.beneficiary_first_name} {o.beneficiary_last_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {o.beneficiary_email} •{" "}
                      {o.beneficiary_role?.replace(/_/g, " ")}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={gatewayLabel(o.gateway || "default")}
                      variant="outlined"
                      sx={{ fontWeight: 600, fontSize: 11 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      size="small"
                      label={`${Number(o.percentage)}%`}
                      color="warning"
                      sx={{ fontWeight: 700 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Delete this override">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={async () => {
                          try {
                            await deleteCommissionOverride(
                              o.user_id,
                              o.beneficiary_user_id,
                              o.gateway || "default",
                            );
                            fetchOverrides();
                            setMsg({
                              severity: "success",
                              text: "Override removed",
                            });
                          } catch (e) {
                            setMsg({
                              severity: "error",
                              text: e.response?.data?.error || "Failed",
                            });
                          }
                        }}
                      >
                        <DeleteRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </Box>
        )}
      </Card>

      {/* ---- Edit override dialog ---- */}
      <Dialog
        open={Boolean(editDialog)}
        onClose={() => setEditDialog(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle fontWeight={600}>Set Commission Override</DialogTitle>
        <DialogContent>
          {editDialog && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="info">
                On <strong>{gatewayLabel(selectedGateway)}</strong>, when{" "}
                <strong>{selectedUser?.first_name || "this user"}</strong> (
                {chainUser?.roleName?.replace(/_/g, " ")}) makes a payment,{" "}
                <strong>
                  {editDialog.firstName} {editDialog.lastName}
                </strong>{" "}
                ({editDialog.roleName?.replace(/_/g, " ")}) will earn this
                percentage instead of the default ({editDialog.defaultRate}%).
              </Alert>
              <TextField
                fullWidth
                label="Commission Percentage (%)"
                type="number"
                inputProps={{ min: 0, max: 100, step: 0.01 }}
                value={editPct}
                onChange={(e) => setEditPct(e.target.value)}
              />
              <Typography variant="caption" color="text.secondary">
                Gateway: {gatewayLabel(selectedGateway)} • Default rate:{" "}
                {editDialog.defaultRate}%
                {gatewayOverrideFor(editDialog.userId) != null &&
                  ` • Current override: ${gatewayOverrideFor(editDialog.userId)}%`}
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditDialog(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveOverride}
            disabled={editSaving}
          >
            {editSaving ? "Saving…" : "Save Override"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}